import { Component, Inject, OnInit, OnDestroy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import * as moment from 'moment';
import { CalendarPreferences } from 'anchor-shared/lib/model/entity/calendarPreferences';
import { timeSelections } from '../../../../../../../../assets/timeSelectionOptions/timeSelectionOptions';
import {
  RatePayee,
  VesselRate,
} from 'anchor-shared/lib/model/entity/vessel-rate';
import { CalendarPreferencesDAO } from '../../../../../../../../core/services/dao/calendarPreferencesDAO';
import { Vessel } from 'anchor-shared/lib/model/entity/vessel';
import { Subscription } from 'rxjs';
import { unsubscribeFromAll } from '../../../../../../../../core/pure-functions/rx-helper';
import { ContainsSubscriptions } from '../../../../../../../../core/services/interfaces/contains-subscriptions';
import { VesselRateDAO } from '../../../../../../../../core/services/dao/vesselRateDAO';
import { TimeWindow } from 'anchor-shared/lib/model/interface/vesselAvailabilityInterfaces';
import { getRateNameForPayouts } from 'anchor-shared/lib/service/pure-functions/get-vessel-rate-name';
import { RateType } from 'anchor-shared/lib/model/enum/vessel-rate-types';
import { UserDAO } from '../../../../../../../../core/services/dao/userDAO';

@Component({
  selector: 'app-schedule-pricing',
  templateUrl: './schedule-pricing.component.html',
  styleUrls: ['./schedule-pricing.component.scss'],
})
export class SchedulePricingComponent
  implements OnInit, OnDestroy, ContainsSubscriptions
{
  public getRateNameForPayouts = getRateNameForPayouts;
  subs: Subscription[] = [];
  rates: VesselRate[];
  isMobile: boolean;
  isAdmin: boolean = false;
  date: any;
  timeSelection: TimeSelectionItem[] = timeSelections;
  isAvailable: boolean;
  hasUnavailableTimeRange: boolean;
  unavailableTime: TimeWindow[] = [{ startTime: null, endTime: null }];
  halfDayDuration: boolean = true;
  fullDayDuration: boolean = true;
  priceAdjustment: number = 0;
  selectedPay: string = 'OwnerPay';
  selectedPayArray: SelectedPayArrayObj[] = [];
  payTotals;
  applyPriceChange: boolean = false;
  productOfferings;
  isEdit: boolean = false;
  adminEditOnly: boolean = false;
  loading: boolean = true;

  constructor(
    @Inject(MAT_DIALOG_DATA) public calendarData: CalendarData,
    private dialogRef: MatDialogRef<SchedulePricingComponent>,
    private calendarPreferencesDAO: CalendarPreferencesDAO,
    private vesselRateDAO: VesselRateDAO,
    private userDAO: UserDAO,
  ) {
    if (this.calendarData.eventDetail.calendarPreferences) {
      this.isEdit = true;
    }
  }

  ngOnInit() {
    const ratesSub = this.vesselRateDAO
      .getByVesselId(this.calendarData.vessel.id)
      .subscribe((rates) => {
        this.rates = rates;
        this.isAvailable = true;
        this.hasUnavailableTimeRange = false;
        this.isMobile = window && window.screen && window.screen.width < 600;
        this.setDate(this.calendarData.eventDetail);
        this.payReportChange();
        if (
          this.calendarData.eventDetail &&
          this.calendarData.eventDetail.calendarPreferences
        ) {
          this.setSavedPreferences(
            this.calendarData.eventDetail.calendarPreferences,
          );
        }
        this.payTotals = this.findTotal();
      });
    this.subs.push(ratesSub);

    this.isAdmin = this.userDAO.currentUserIs('Admin');
  }

  canEdit() {
    if (this.isAdmin) {
      return true;
    } else if (!this.isAdmin && !this.adminEditOnly) {
      return true;
    }

    return false;
  }

  setSavedPreferences(calendarPreferences: CalendarPreferences) {
    if (calendarPreferences) {
      this.isAvailable = calendarPreferences.available;
      this.unavailableTime = calendarPreferences.unavailableTimes;
      this.hasUnavailableTimeRange =
        calendarPreferences.unavailableTimes[0].startTime !== null
          ? true
          : false;
      this.halfDayDuration =
        calendarPreferences.charterDurationOfferings.halfDayDuration;
      this.fullDayDuration =
        calendarPreferences.charterDurationOfferings.fullDayDuration;
      this.priceAdjustment = calendarPreferences.priceAdjustmentPercentage;
      this.adminEditOnly = calendarPreferences.adminEditOnly;
      if (calendarPreferences.charterDurationOfferings) {
        this.productOfferings = calendarPreferences.charterDurationOfferings;
        this.applyPriceChange =
          calendarPreferences.charterDurationOfferings.applyPriceChange;
      }
      this.payReportChange();
    }
  }

  setDate(dateTime) {
    let start;
    let end;
    if (dateTime.calendarPreferences) {
      start = moment.utc(dateTime.calendarPreferences.fromDate);
      end = moment.utc(dateTime.calendarPreferences.toDate);
    } else {
      start = dateTime.start;
      end = dateTime.end;
    }
    if (end - start === 86400000) {
      this.date = start.format('MMMM Do, YYYY');
    } else if (end - start > 86400000 && (end - start) % 86400000 === 0) {
      this.date = `${start.format('MMMM Do, YYYY')} - ${end
        .subtract(1, 'days')
        .format('MMMM Do, YYYY')}`;
      end.add(1, 'days').format('MMMM Do, YYYY');
    } else if (end - start <= 86400000 && (end - start) % 86400000 !== 0) {
      this.date = start.format('MMMM Do, YYYY');
      this.calendarData.eventDetail.end.add(1, 'day');
    } else {
      this.date = 'dateError';
    }
  }

  formatLabel(value: number) {
    return value + '%';
  }

  addTimeRange() {
    this.unavailableTime.push({ startTime: null, endTime: null });
  }

  deleteRow(index) {
    if (this.unavailableTime.length > 1) {
      this.unavailableTime.splice(index, 1);
    } else {
      this.unavailableTime[index].startTime = null;
      this.unavailableTime[index].endTime = null;
    }
  }

  calculatePriceForPay(
    pay: number,
    isOwnerPay: boolean,
    markup: number = 0,
    tax: number = 0,
  ) {
    let priceAdjustmentAsPercent = this.priceAdjustment / 100;
    let price = pay;
    if (isOwnerPay) {
      price += price * priceAdjustmentAsPercent;
      return price;
    } else {
      price += price * priceAdjustmentAsPercent;
      price += price * markup;
      price += price * tax;
      return price;
    }
  }

  calculateBeforePrice(rate: number, markup: number, tax: number): number {
    let price = rate;
    price += rate * markup;
    price += price * tax;
    return price;
  }

  formatPayItem(rateItem: VesselRate, boatOwnerOnly: boolean) {
    if (
      rateItem.rateType === 'Custom' &&
      this.selectedPay === 'OwnerPay' &&
      boatOwnerOnly
    ) {
      this.selectedPayArray.push({
        name: this.getRateNameForPayouts(rateItem),
        fullDay: rateItem.customRate,
        fullDayWithPriceAdjustment: rateItem.customRate,
        halfDay: rateItem.customRate,
        halfDayWithPriceAdjustment: rateItem.customRate,
      });
    } else if (
      rateItem.rateType === 'Custom' &&
      this.selectedPay === 'OwnerPay' &&
      !boatOwnerOnly
    ) {
      this.selectedPayArray.push({
        name: this.getRateNameForPayouts(rateItem),
        fullDay: rateItem.customRate,
        fullDayWithPriceAdjustment: this.calculatePriceForPay(
          rateItem.customRate,
          true,
        ),
        halfDay: rateItem.customRate,
        halfDayWithPriceAdjustment: this.calculatePriceForPay(
          rateItem.customRate,
          true,
        ),
      });
    } else if (
      rateItem.rateType === 'Custom' &&
      this.selectedPay === 'CustomerPrice' &&
      !boatOwnerOnly
    ) {
      this.selectedPayArray.push({
        name: this.getRateNameForPayouts(rateItem),
        fullDay: this.calculateBeforePrice(
          rateItem.customRate,
          rateItem.markupPercentage,
          rateItem.taxPercentage,
        ),
        fullDayWithPriceAdjustment: this.calculatePriceForPay(
          rateItem.customRate,
          false,
          rateItem.markupPercentage,
          rateItem.taxPercentage,
        ),
        halfDay: this.calculateBeforePrice(
          rateItem.customRate,
          rateItem.markupPercentage,
          rateItem.taxPercentage,
        ),
        halfDayWithPriceAdjustment: this.calculatePriceForPay(
          rateItem.customRate,
          false,
          rateItem.markupPercentage,
          rateItem.taxPercentage,
        ),
      });
    } else if (
      rateItem.rateType === 'Custom' &&
      this.selectedPay === 'CustomerPrice' &&
      boatOwnerOnly
    ) {
      this.selectedPayArray.push({
        name: this.getRateNameForPayouts(rateItem),
        fullDay: this.calculateBeforePrice(
          rateItem.customRate,
          rateItem.markupPercentage,
          rateItem.taxPercentage,
        ),
        fullDayWithPriceAdjustment: this.calculateBeforePrice(
          rateItem.customRate,
          rateItem.markupPercentage,
          rateItem.taxPercentage,
        ),
        halfDay: this.calculateBeforePrice(
          rateItem.customRate,
          rateItem.markupPercentage,
          rateItem.taxPercentage,
        ),
        halfDayWithPriceAdjustment: this.calculateBeforePrice(
          rateItem.customRate,
          rateItem.markupPercentage,
          rateItem.taxPercentage,
        ),
      });
    } else if (
      boatOwnerOnly &&
      rateItem.ratePayee === RatePayee.BOAT_OWNER &&
      this.selectedPay === 'CustomerPrice' &&
      rateItem.name === null &&
      rateItem.rateType === 'Owner'
    ) {
      this.selectedPayArray.push({
        name: this.getRateNameForPayouts(rateItem),
        fullDay: this.calculateBeforePrice(
          rateItem.fullDayRate,
          rateItem.markupPercentage,
          rateItem.taxPercentage,
        ),
        fullDayWithPriceAdjustment: this.calculatePriceForPay(
          rateItem.fullDayRate,
          false,
          rateItem.markupPercentage,
          rateItem.taxPercentage,
        ),
        halfDay: this.calculateBeforePrice(
          rateItem.halfDayRate,
          rateItem.markupPercentage,
          rateItem.taxPercentage,
        ),
        halfDayWithPriceAdjustment: this.calculatePriceForPay(
          rateItem.halfDayRate,
          false,
          rateItem.markupPercentage,
          rateItem.taxPercentage,
        ),
      });
    } else if (rateItem.rateType === RateType.Cleaning) {
      this.selectedPayArray.push({
        name: this.getRateNameForPayouts(rateItem),
        fullDay: this.calculateBeforePrice(
          rateItem.customRate,
          rateItem.markupPercentage,
          rateItem.taxPercentage,
        ),
        fullDayWithPriceAdjustment: this.calculatePriceForPay(
          rateItem.customRate,
          false,
          rateItem.markupPercentage,
          rateItem.taxPercentage,
        ),
        halfDay: this.calculateBeforePrice(
          rateItem.customRate,
          rateItem.markupPercentage,
          rateItem.taxPercentage,
        ),
        halfDayWithPriceAdjustment: this.calculatePriceForPay(
          rateItem.customRate,
          false,
          rateItem.markupPercentage,
          rateItem.taxPercentage,
        ),
      });
    } else if (
      boatOwnerOnly &&
      rateItem.ratePayee === RatePayee.BOAT_OWNER &&
      this.selectedPay === 'CustomerPrice' &&
      rateItem.name === null &&
      rateItem.rateType !== 'Owner'
    ) {
      this.selectedPayArray.push({
        name: this.getRateNameForPayouts(rateItem),
        fullDay: this.calculateBeforePrice(
          rateItem.fullDayRate,
          rateItem.markupPercentage,
          rateItem.taxPercentage,
        ),
        fullDayWithPriceAdjustment: this.calculateBeforePrice(
          rateItem.fullDayRate,
          rateItem.markupPercentage,
          rateItem.taxPercentage,
        ),
        halfDay: this.calculateBeforePrice(
          rateItem.halfDayRate,
          rateItem.markupPercentage,
          rateItem.taxPercentage,
        ),
        halfDayWithPriceAdjustment: this.calculateBeforePrice(
          rateItem.halfDayRate,
          rateItem.markupPercentage,
          rateItem.taxPercentage,
        ),
      });
    } else if (
      boatOwnerOnly &&
      rateItem.ratePayee === RatePayee.BOAT_OWNER &&
      this.selectedPay === 'OwnerPay' &&
      rateItem.rateType === 'Owner' &&
      rateItem.name === null
    ) {
      this.selectedPayArray.push({
        name: this.getRateNameForPayouts(rateItem),
        fullDay: rateItem.fullDayRate,
        fullDayWithPriceAdjustment: this.calculatePriceForPay(
          rateItem.fullDayRate,
          true,
        ),
        halfDay: rateItem.halfDayRate,
        halfDayWithPriceAdjustment: this.calculatePriceForPay(
          rateItem.halfDayRate,
          true,
        ),
      });
    } else if (
      boatOwnerOnly &&
      rateItem.ratePayee === RatePayee.BOAT_OWNER &&
      this.selectedPay === 'OwnerPay' &&
      rateItem.rateType !== 'Owner' &&
      rateItem.name === null
    ) {
      this.selectedPayArray.push({
        name: this.getRateNameForPayouts(rateItem),
        fullDay: rateItem.fullDayRate,
        fullDayWithPriceAdjustment: rateItem.fullDayRate,
        halfDay: rateItem.halfDayRate,
        halfDayWithPriceAdjustment: rateItem.halfDayRate,
      });
    } else if (
      boatOwnerOnly &&
      rateItem.ratePayee === RatePayee.BOAT_OWNER &&
      this.selectedPay !== 'OwnerPay' &&
      rateItem.rateType !== 'Owner' &&
      rateItem.name !== null
    ) {
      this.selectedPayArray.push({
        name: this.getRateNameForPayouts(rateItem),
        fullDay: this.calculateBeforePrice(
          rateItem.fullDayRate,
          rateItem.markupPercentage,
          rateItem.taxPercentage,
        ),
        fullDayWithPriceAdjustment: this.calculateBeforePrice(
          rateItem.fullDayRate,
          rateItem.markupPercentage,
          rateItem.taxPercentage,
        ),
        halfDay: this.calculateBeforePrice(
          rateItem.halfDayRate,
          rateItem.markupPercentage,
          rateItem.taxPercentage,
        ),
        halfDayWithPriceAdjustment: this.calculateBeforePrice(
          rateItem.halfDayRate,
          rateItem.markupPercentage,
          rateItem.taxPercentage,
        ),
      });
    } else if (
      boatOwnerOnly &&
      rateItem.ratePayee === RatePayee.BOAT_OWNER &&
      this.selectedPay === 'OwnerPay' &&
      rateItem.name !== null
    ) {
      this.selectedPayArray.push({
        name: this.getRateNameForPayouts(rateItem),
        fullDay: rateItem.fullDayRate,
        fullDayWithPriceAdjustment: rateItem.fullDayRate,
        halfDay: rateItem.halfDayRate,
        halfDayWithPriceAdjustment: rateItem.halfDayRate,
      });
    } else if (
      boatOwnerOnly &&
      rateItem.ratePayee !== RatePayee.BOAT_OWNER &&
      this.selectedPay === 'CustomerPrice'
    ) {
      this.selectedPayArray.push({
        name: this.getRateNameForPayouts(rateItem),
        fullDay: this.calculateBeforePrice(
          rateItem.fullDayRate,
          rateItem.markupPercentage,
          rateItem.taxPercentage,
        ),
        fullDayWithPriceAdjustment: this.calculateBeforePrice(
          rateItem.fullDayRate,
          rateItem.markupPercentage,
          rateItem.taxPercentage,
        ),
        halfDay: this.calculateBeforePrice(
          rateItem.halfDayRate,
          rateItem.markupPercentage,
          rateItem.taxPercentage,
        ),
        halfDayWithPriceAdjustment: this.calculateBeforePrice(
          rateItem.halfDayRate,
          rateItem.markupPercentage,
          rateItem.taxPercentage,
        ),
      });
    } else if (
      boatOwnerOnly &&
      rateItem.ratePayee !== RatePayee.BOAT_OWNER &&
      this.selectedPay === 'OwnerPay'
    ) {
      this.selectedPayArray.push({
        name: this.getRateNameForPayouts(rateItem),
        fullDay: rateItem.fullDayRate,
        fullDayWithPriceAdjustment: rateItem.fullDayRate,
        halfDay: rateItem.halfDayRate,
        halfDayWithPriceAdjustment: rateItem.halfDayRate,
      });
    } else if (!boatOwnerOnly && this.selectedPay === 'CustomerPrice') {
      this.selectedPayArray.push({
        name: this.getRateNameForPayouts(rateItem),
        fullDay: this.calculateBeforePrice(
          rateItem.fullDayRate,
          rateItem.markupPercentage,
          rateItem.taxPercentage,
        ),
        fullDayWithPriceAdjustment: this.calculatePriceForPay(
          rateItem.fullDayRate,
          false,
          rateItem.markupPercentage,
          rateItem.taxPercentage,
        ),
        halfDay: this.calculateBeforePrice(
          rateItem.halfDayRate,
          rateItem.markupPercentage,
          rateItem.taxPercentage,
        ),
        halfDayWithPriceAdjustment: this.calculatePriceForPay(
          rateItem.halfDayRate,
          false,
          rateItem.markupPercentage,
          rateItem.taxPercentage,
        ),
      });
    } else if (!boatOwnerOnly && this.selectedPay === 'OwnerPay') {
      this.selectedPayArray.push({
        name: this.getRateNameForPayouts(rateItem),
        fullDay: rateItem.fullDayRate,
        fullDayWithPriceAdjustment: this.calculatePriceForPay(
          rateItem.fullDayRate,
          true,
        ),
        halfDay: rateItem.halfDayRate,
        halfDayWithPriceAdjustment: this.calculatePriceForPay(
          rateItem.halfDayRate,
          true,
        ),
      });
    } else {
      console.log('Error with rateItem', boatOwnerOnly, rateItem);
    }
  }

  payReportChange() {
    this.selectedPayArray = [];
    this.rates.map((rateItem) => {
      if (
        this.selectedPay === 'OwnerPay' &&
        rateItem.ratePayee === RatePayee.BOAT_OWNER &&
        rateItem.id > 0
      ) {
        if (this.priceAdjustment <= 0) {
          this.formatPayItem(rateItem, true);
        } else {
          this.formatPayItem(rateItem, false);
        }
      } else if (this.selectedPay === 'CustomerPrice' && rateItem.id > 0) {
        if (this.priceAdjustment <= 0) {
          this.formatPayItem(rateItem, true);
        } else {
          this.formatPayItem(rateItem, false);
        }
      }
    });
    this.payTotals = this.findTotal();
  }

  findTotal(): PayTotal {
    let payTotal = {
      fullDay: 0,
      fullDayWithPriceAdjustment: 0,
      halfDay: 0,
      halfDayWithPriceAdjustment: 0,
    };
    this.selectedPayArray.map((payItem) => {
      payTotal.fullDay += payItem.fullDay;
      payTotal.fullDayWithPriceAdjustment += payItem.fullDayWithPriceAdjustment;
      payTotal.halfDay += payItem.halfDay;
      payTotal.halfDayWithPriceAdjustment += payItem.halfDayWithPriceAdjustment;
    });
    return payTotal;
  }

  save() {
    if (!this.hasUnavailableTimeRange) {
      this.unavailableTime = [{ startTime: null, endTime: null }];
    }

    if (
      this.unavailableTime[this.unavailableTime.length - 1].endTime === null
    ) {
      this.deleteRow(this.unavailableTime.length - 1);
    }

    const preferenceData: Partial<preferenceDataItem> = {
      vesselId: this.calendarData.vessel.id,
      available: this.isAvailable,
      unavailableTimes: this.unavailableTime,
      charterDurationOfferings: {
        applyPriceChange: this.applyPriceChange,
        halfDayDuration: this.halfDayDuration,
        fullDayDuration: this.fullDayDuration,
      },
      priceAdjustmentPercentage: this.priceAdjustment,
    };

    if (!this.calendarData.eventDetail.calendarPreferences) {
      preferenceData.fromDate = this.calendarData.eventDetail.start
        .startOf('day')
        .format('YYYY-MM-DD');
      preferenceData.toDate = this.calendarData.eventDetail.end
        .endOf('day')
        .format('YYYY-MM-DD');
    } else {
      preferenceData.id = this.calendarData.eventDetail.calendarPreferencesId;
    }

    preferenceData.adminEditOnly = this.adminEditOnly;

    let data = {
      preferenceData: preferenceData,
      deleted: false,
      cancel: false,
    };
    this.dialogRef.close(data);
  }

  cancel() {
    let data = {
      preferenceData: null,
      deleted: false,
      cancel: true,
    };
    this.dialogRef.close(data);
  }

  removeSetting() {
    let data = {
      preferenceData: this.calendarData.eventDetail.calendarPreferences,
      deleted: true,
      cancel: false,
    };
    const calendarPreferencesDeleteSub = this.calendarPreferencesDAO
      .deleteById(
        this.calendarData.vessel.id,
        this.calendarData.eventDetail.calendarPreferencesId,
      )
      .subscribe((didDelete) => {
        if (didDelete.deleted) {
          this.dialogRef.close(data);
        }
      });
    this.subs.push(calendarPreferencesDeleteSub);
  }

  priceAdjustmentMinus() {
    if (this.priceAdjustment === -100) {
      return;
    }
    this.priceAdjustment = this.priceAdjustment - 5;
    this.payReportChange();
  }

  priceAdjustmentPlus() {
    if (this.priceAdjustment === 100) {
      return;
    }
    this.priceAdjustment = this.priceAdjustment + 5;
    this.payReportChange();
  }

  ngOnDestroy(): void {
    unsubscribeFromAll(this.subs);
  }
}

export interface preferenceDataItem {
  id?: number;
  vesselId: number;
  fromDate: string;
  toDate: string;
  available: boolean;
  unavailableTimes: TimeWindow[];
  charterDurationOfferings: {
    applyPriceChange: boolean;
    halfDayDuration: boolean;
    fullDayDuration: boolean;
  };
  priceAdjustmentPercentage: number;
  adminEditOnly: boolean;
}

export interface TimeSelectionItem {
  time: string;
}

export interface PayTotal {
  fullDay: number;
  fullDayWithPriceAdjustment: number;
  halfDay: number;
  halfDayWithPriceAdjustment: number;
}

export interface SelectedPayArrayObj {
  name: string;
  fullDay: number;
  fullDayWithPriceAdjustment: number;
  halfDay: number;
  halfDayWithPriceAdjustment: number;
}

export interface CalendarData {
  eventDetail: any;
  vessel: Vessel;
}
